import type { HoldChoice } from '@/types/custom-playbooks';
import {
  HoldDecisionCancelled,
  holdsBody,
} from '@/containers/playbooks/custom-builder/held-records';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  useAgentSnapshot,
  usePatchAgentMutation,
} from '@/store/features/gtm-agents.feature';
import type { AgentDto, PatchAgentBody } from '@/types/gtm-agents';
import { undoBodyFor, type Change } from './agent-edit.util';
import { serverReason } from './server-reason.util';

/** The panel's save state, where Gumloop has its Save button. */
export type SaveState =
  | { status: 'idle' }
  | { status: 'saving' }
  | { status: 'saved' }
  | { status: 'failed'; message: string }
  /** One short sentence about the page, neither a save nor a failure. */
  | { status: 'note'; message: string };

export interface PatchOptions {
  /** False for a write Undo must not take back (finishing the setup). */
  undoable?: boolean;
  /** The words when the server gives no reason. */
  failure?: string;
  /** Goes out while the panel is locked (Set up manually unlocking it). */
  unlocks?: boolean;
}

export interface AgentEditor {
  agentId: string;
  /** Writes one change; true when the server took it. */
  patch: (change: Change, options?: PatchOptions) => Promise<boolean>;
  /** Takes back this page's last edit; Clu's changes are never on the stack. */
  undo: () => Promise<boolean>;
  canUndo: boolean;
  saveState: SaveState;
  /** A value the page refused before sending it, shown as a failure. */
  fail: (message: string) => void;
  /**
   * Another write (accepting a proposal) under the same save state: Saving
   * while it is out, then Saved or `reason(error)`. True when it went through.
   */
  run: (
    work: () => Promise<unknown>,
    reason: (error: unknown) => string
  ) => Promise<boolean>;
  /** One short sentence in the save state's place. */
  note: (message: string) => void;
}

/** How many of this page's own edits Undo remembers. */
export const UNDO_LIMIT = 20;

/**
 * The agent panel's writes: each change goes out through the ordered PATCH
 * (`patchAgent` keeps one agent's writes in order), the save state follows
 * them (Saving while any is out, then Saved or the server's reason), and the
 * write that takes each successful edit back is kept, the last 20.
 */
export function useAgentEditorState(
  agentId: string,
  agent: AgentDto | undefined,
  /**
   * Read only while Clu sets the agent up: no edit from this page goes out,
   * except the one that unlocks it.
   */
  locked = false,
  runWithHolds?: <T>(action: (holds?: HoldChoice) => Promise<T>) => Promise<T>,
  authority?: { organizationId: string; isCurrent: () => boolean }
): AgentEditor {
  const [patchAgent] = usePatchAgentMutation();
  const snapshot = useAgentSnapshot(agentId);
  const [saveState, setSaveState] = useState<SaveState>({ status: 'idle' });
  const [stack, setStack] = useState<PatchAgentBody[]>([]);
  const stackRef = useRef(stack);
  stackRef.current = stack;
  const agentRef = useRef(agent);
  agentRef.current = agent;
  const authorityRef = useRef(authority);
  authorityRef.current = authority;
  const pending = useRef(0);
  const failure = useRef<string | null>(null);

  // Another agent starts with a clean history. Not on the first render, so a
  // state a child set while mounting (a proposal's note) stays.
  const seenAgent = useRef(agentId);
  useEffect(() => {
    if (seenAgent.current === agentId) return;
    seenAgent.current = agentId;
    setStack([]);
    setSaveState({ status: 'idle' });
    pending.current = 0;
    failure.current = null;
  }, [agentId]);

  // Another owner (``useAgentOwner``): an edit made under the one before isn't
  // taken back, since its undo would point connections back at them. Once
  // the change has landed: a refused one leaves the history as it was.
  const ownerUserId = agent?.ownerUserId;
  const ownerPending = agent?.ownerChangePending === true;
  const seenOwner = useRef(ownerUserId);
  useEffect(() => {
    if (ownerPending || seenOwner.current === ownerUserId) return;
    const before = seenOwner.current;
    seenOwner.current = ownerUserId;
    if (before !== undefined) setStack([]);
  }, [ownerUserId, ownerPending]);

  const run = useCallback(
    async (
      work: () => Promise<unknown>,
      reason: (error: unknown) => string
    ): Promise<boolean> => {
      pending.current += 1;
      setSaveState({ status: 'saving' });
      let ok = false;
      try {
        await work();
        ok = true;
      } catch (error) {
        failure.current = reason(error);
      }
      pending.current = Math.max(0, pending.current - 1);
      if (pending.current === 0) {
        setSaveState(
          failure.current
            ? { status: 'failed', message: failure.current }
            : { status: 'saved' }
        );
        failure.current = null;
      }
      return ok;
    },
    []
  );

  const send = useCallback(
    async (
      body: PatchAgentBody,
      fallback: string,
      unlocks = false
    ): Promise<boolean> => {
      let cancelled = false;
      const captured = authority;
      const action = (holds?: HoldChoice) => {
        if (
          (lockedRef.current && !unlocks) ||
          (captured &&
            (authorityRef.current?.organizationId !== captured.organizationId ||
              !captured.isCurrent()))
        )
          throw new HoldDecisionCancelled();
        return patchAgent({
          id: agentId,
          ...(captured ? { organizationId: captured.organizationId } : {}),
          body: { ...body, ...holdsBody(holds) },
        }).unwrap();
      };
      const ok = await run(
        () => (runWithHolds ? runWithHolds(action) : action()),
        (error) => {
          if (error instanceof HoldDecisionCancelled) {
            cancelled = true;
            return '';
          }
          return serverReason(error, fallback);
        }
      );
      if (cancelled) setSaveState({ status: 'idle' });
      return ok;
    },
    [run, patchAgent, agentId, runWithHolds, authority]
  );

  const lockedRef = useRef(locked);
  lockedRef.current = locked;

  const patch = useCallback(
    async (change: Change, options: PatchOptions = {}): Promise<boolean> => {
      if (lockedRef.current && !options.unlocks) return false;
      const current = snapshot() ?? agentRef.current;
      if (!current) return false;
      const body = typeof change === 'function' ? change(current) : change;
      const back =
        options.undoable === false ? null : undoBodyFor(body, current);
      const ok = await send(
        body,
        options.failure ?? 'Could not save the change.',
        options.unlocks
      );
      // Not when the owner changed while it was out: its undo was written
      // for the one before.
      if (ok && back && agentRef.current?.ownerUserId === current.ownerUserId)
        setStack((previous) => [...previous.slice(1 - UNDO_LIMIT), back]);
      return ok;
    },
    [snapshot, send]
  );

  // No undo while an owner change is out: it would go after the change and
  // could point a connection back at the old owner.
  const ownerPendingRef = useRef(ownerPending);
  ownerPendingRef.current = ownerPending;

  const undo = useCallback(async (): Promise<boolean> => {
    const last = stackRef.current[stackRef.current.length - 1];
    if (!last || lockedRef.current || ownerPendingRef.current) return false;
    setStack((previous) => previous.slice(0, -1));
    const ok = await send(last, 'Could not undo the change.');
    // Kept for another try when the server refused it.
    if (!ok) setStack((previous) => [...previous, last]);
    return ok;
  }, [send]);

  const fail = useCallback((message: string) => {
    setSaveState({ status: 'failed', message });
  }, []);

  const note = useCallback((message: string) => {
    setSaveState({ status: 'note', message });
  }, []);

  return {
    agentId,
    patch,
    undo,
    canUndo: stack.length > 0 && !locked && !ownerPending,
    saveState,
    fail,
    run,
    note,
  };
}

export const AgentEditorContext = createContext<AgentEditor | null>(null);

/** The panel's editor, from the workspace. */
export function useAgentEditor(): AgentEditor {
  const editor = useContext(AgentEditorContext);
  if (!editor)
    throw new Error('useAgentEditor needs the agent workspace around it');
  return editor;
}

/**
 * Cmd+Z (Ctrl+Z) takes back the last edit while focus is not in a text
 * field. Off on the Play tab, where the builder's canvas owns Cmd+Z.
 */
export function useUndoShortcut(undo: () => unknown, enabled: boolean) {
  useEffect(() => {
    if (!enabled) return;
    const onKey = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.shiftKey) return;
      if (event.key.toLowerCase() !== 'z') return;
      const target = event.target;
      if (
        target instanceof Element &&
        target.closest(
          'input, textarea, select, [contenteditable="true"], [contenteditable=""]'
        )
      )
        return;
      event.preventDefault();
      void undo();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, enabled]);
}
